<template>
  <div class="date-range-picker">
    <button class="date-range-button" :class="{ 'mobile': isMobile }" @click="toggleDatePicker">
      <i class="bi bi-calendar3 me-2"></i>
      {{ formatDateRange }}
      <i class="bi bi-chevron-down ms-2"></i>
    </button>
    <div v-if="showDatePicker" class="date-picker-dropdown" :class="`align-${alignment}`">
      <div class="date-picker-header">
        <button @click="previousMonth" class="month-nav-btn">‹</button>
        <span class="current-month">{{ currentMonthYear }}</span>
        <button @click="nextMonth" class="month-nav-btn">›</button>
      </div>
      <div class="calendar-grid">
        <div class="weekday-header" v-for="day in weekDays" :key="day">{{ day }}</div>
        <div
          v-for="date in calendarDates"
          :key="date.key"
          class="calendar-date"
          :class="{
            'other-month': !date.isCurrentMonth,
            'selected': isDateSelected(date.date),
            'in-range': isDateInRange(date.date),
            'disabled': isDateDisabled(date.date)
          }"
          @click="selectDate(date)"
        >
          {{ date.day }}
        </div>
      </div>
      <div class="date-picker-footer">
        <button class="btn-apply" @click="applyDateSelection">{{ $t('dashboard.date_range.apply') }}</button>
        <button class="btn-cancel" @click="cancelDateSelection">{{ $t('dashboard.date_range.cancel') }}</button>
      </div>
    </div>

    <ErrorDialog
      v-model="showErrorDialogState"
      :message="errorMessage"
      @confirm="closeErrorDialog"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ErrorDialog from '@/components/common/ErrorDialog.vue';
import { useIsMobile } from '@/utils/DeviceUtil';
import { colors } from '../../../../consts/colors';

const { t: $t } = useI18n();
const { isMobile } = useIsMobile();

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      startDate: new Date(2024, 9, 1),
      endDate: new Date(2024, 9, 7)
    })
  },
  alignment: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right'].includes(value)
  },
  maxDays: {
    type: Number,
    default: 31
  },
  minDate: {
    type: Date,
    default: null
  },
  maxDate: {
    type: Date,
    default: null
  }
});

const emit = defineEmits(['update:modelValue']);

// Error dialog state
const showErrorDialogState = ref(false);
const errorMessage = ref('');

const showDatePicker = ref(false);
const tempStartDate = ref(null);
const tempEndDate = ref(null);
const currentMonth = ref(new Date()); // Default to current month

// Weekdays
const weekDays = computed(() => [
  $t('common.calendar.weekdays.sun'),
  $t('common.calendar.weekdays.mon'),
  $t('common.calendar.weekdays.tue'),
  $t('common.calendar.weekdays.wed'),
  $t('common.calendar.weekdays.thu'),
  $t('common.calendar.weekdays.fri'),
  $t('common.calendar.weekdays.sat'),
]);

// Format date range for display
const formatDateRange = computed(() => {
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };
  return `${formatDate(props.modelValue.startDate)} 〜 ${formatDate(props.modelValue.endDate)}`;
});

// Current month/year display
const currentMonthYear = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = String(currentMonth.value.getMonth() + 1).padStart(2, '0');
  return `${year}/${month}`;
});

// Generate calendar dates
const calendarDates = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = currentMonth.value.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDayOfWeek = firstDay.getDay();
  const dates = [];

  // Previous month dates
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const day = prevMonthLastDay - i;
    dates.push({
      day,
      date: new Date(year, month - 1, day),
      isCurrentMonth: false,
      key: `prev-${day}`
    });
  }

  // Current month dates
  for (let day = 1; day <= lastDay.getDate(); day++) {
    dates.push({
      day,
      date: new Date(year, month, day),
      isCurrentMonth: true,
      key: `current-${day}`
    });
  }

  // Next month dates
  const remainingDays = 42 - dates.length; // 6 weeks * 7 days
  for (let day = 1; day <= remainingDays; day++) {
    dates.push({
      day,
      date: new Date(year, month + 1, day),
      isCurrentMonth: false,
      key: `next-${day}`
    });
  }

  return dates;
});

// Date picker methods
const toggleDatePicker = () => {
  showDatePicker.value = !showDatePicker.value;
  if (showDatePicker.value) {
    tempStartDate.value = null;
    tempEndDate.value = null;
  }
};

const previousMonth = () => {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() - 1,
    1
  );
};

const nextMonth = () => {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() + 1,
    1
  );
};

const selectDate = (dateObj) => {
  if (!dateObj.isCurrentMonth || isDateDisabled(dateObj.date)) return;

  if (!tempStartDate.value || (tempStartDate.value && tempEndDate.value)) {
    // Start new selection
    tempStartDate.value = dateObj.date;
    tempEndDate.value = null;
  } else {
    // Select end date
    if (dateObj.date < tempStartDate.value) {
      tempEndDate.value = tempStartDate.value;
      tempStartDate.value = dateObj.date;
    } else {
      tempEndDate.value = dateObj.date;
    }

    // Check if range exceeds max days
    const daysDiff = Math.floor((tempEndDate.value - tempStartDate.value) / (1000 * 60 * 60 * 24));
    if (daysDiff > props.maxDays - 1) {
      // Show error dialog for exceeding max days
      showErrorDialog($t('dashboard.date_range.alert.max_range', { days: props.maxDays }));
      tempStartDate.value = dateObj.date;
      tempEndDate.value = null;
    }
  }
};

const isDateSelected = (date) => {
  if (!tempStartDate.value) return false;
  const dateStr = date.toDateString();
  const startStr = tempStartDate.value.toDateString();
  const endStr = tempEndDate.value?.toDateString();
  return dateStr === startStr || dateStr === endStr;
};

const isDateInRange = (date) => {
  if (!tempStartDate.value || !tempEndDate.value) return false;
  return date > tempStartDate.value && date < tempEndDate.value;
};

const isDateDisabled = (date) => {
  // Normalize date to start of day for accurate comparison
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  // Check if date is before minDate
  if (props.minDate) {
    const minDateNormalized = new Date(props.minDate);
    minDateNormalized.setHours(0, 0, 0, 0);
    if (normalizedDate < minDateNormalized) {
      return true;
    }
  }

  // Check if date is after maxDate
  if (props.maxDate) {
    const maxDateNormalized = new Date(props.maxDate);
    maxDateNormalized.setHours(0, 0, 0, 0);
    if (normalizedDate > maxDateNormalized) {
      return true;
    }
  }

  // If no min/max date is provided, fallback to default behavior
  // Allow dates up to the end of 2 years from now
  if (!props.minDate && !props.maxDate) {
    const today = new Date();
    const maxYear = today.getFullYear() + 2;
    const defaultMaxDate = new Date(maxYear, 11, 31); // December 31st of 2 years from now
    defaultMaxDate.setHours(23, 59, 59, 999);
    return date > defaultMaxDate;
  }

  return false;
};

const cancelDateSelection = () => {
  showDatePicker.value = false;
  tempStartDate.value = null;
  tempEndDate.value = null;
};

const applyDateSelection = () => {
  if (tempStartDate.value && tempEndDate.value) {
    emit('update:modelValue', {
      startDate: tempStartDate.value,
      endDate: tempEndDate.value
    });
    showDatePicker.value = false;
  } else {
    showErrorDialog($t('dashboard.date_range.alert.select_dates'));
  }
};

const showErrorDialog = (message) => {
  errorMessage.value = message;
  showErrorDialogState.value = true;
};

const closeErrorDialog = () => {
  showErrorDialogState.value = false;
  errorMessage.value = '';
};
</script>

<style lang="scss" scoped>
.date-range-picker {
  position: relative;

  .date-range-button {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    background: v-bind('colors.white');
    border: 1px solid v-bind('colors.gray[200]');
    border-radius: 6px;
    font-size: 14px;
    color: v-bind('colors.textPrimary');
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      border-color: v-bind('colors.blue[500]');
    }

    &.mobile {
      width: 100%;
      justify-content: space-between;
    }
  }

  .date-picker-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: v-bind('colors.white');
    border: 1px solid v-bind('colors.gray[200]');
    border-radius: 8px;
    box-shadow: 0 4px 12px v-bind('colors.shadowMedium');
    padding: 16px;
    z-index: 10000;
    min-width: 320px;

    &.align-left {
      right: auto;
      left: 0;
    }

    &.align-right {
      left: auto;
      right: 0;
    }
  }

  .date-picker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .month-nav-btn {
      background: none;
      border: none;
      font-size: 20px;
      color: v-bind('colors.gray[500]');
      cursor: pointer;
      padding: 4px 12px;

      &:hover {
        color: v-bind('colors.textPrimary');
      }
    }

    .current-month {
      font-size: 16px;
      font-weight: 600;
      color: v-bind('colors.textPrimary');
    }
  }

  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    margin-bottom: 16px;

    .weekday-header {
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      color: v-bind('colors.gray[500]');
      padding: 8px 4px;
    }

    .calendar-date {
      text-align: center;
      padding: 8px 4px;
      font-size: 14px;
      color: v-bind('colors.textPrimary');
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.2s;

      &:hover:not(.disabled):not(.other-month) {
        background-color: v-bind('colors.gray[300]');
      }

      &.other-month {
        color: v-bind('colors.disabled');
        cursor: default;
      }

      &.selected {
        background-color: v-bind('colors.blue[500]');
        color: v-bind('colors.white');
        font-weight: 600;
      }

      &.in-range {
        background-color: v-bind('colors.chartBlue[200]');
        color: v-bind('colors.textPrimary');
      }

      &.disabled {
        color: v-bind('colors.disabled');
        cursor: not-allowed;
      }
    }
  }

  .date-picker-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid v-bind('colors.gray[200]');

    button {
      padding: 6px 16px;
      border-radius: 6px;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .btn-cancel {
      background: v-bind('colors.white');
      border: 1px solid v-bind('colors.gray[200]');
      color: v-bind('colors.gray[500]');

      &:hover {
        background-color: v-bind('colors.gray[300]');
      }
    }

    .btn-apply {
      background: v-bind('colors.btnBgColorPrimary');
      border: none;
      color: v-bind('colors.btnTextColorPrimary');

      &:hover {
        background-color: v-bind('colors.btnBgColorHover');
        color: v-bind('colors.btnTextColorHover');
      }
    }
  }
}
</style>
